import React, { useCallback, useEffect, useState } from 'react';
import {
  ATMField,
  ATMHeader,
  ATMSegment,
  ATMGrid,
  ATMForm,
  ATMRadio,
  ATMInput,
  formatPhoneNumber,
} from 'shared-it-appmod-ui';
import { DEFAULT_EMPLOYEE_NUMBER } from 'src/constants';
import Lang from 'src/libraries/language';
import DropdownEmployee from 'src/components/atoms/dropdown/dropdown-employee/dropdown-employee.component';
import { useEmployeeContext } from 'src/contexts/employee.context';
import { IEmployee } from 'src/models/employee.model';
import BlockEmployee from 'src/components/atoms/block-employee/block-employee.component';
import { IRequestFormMethods } from 'src/models/request.model';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';
import styles from './flight-request-requestor-form.module.scss';

type IProps = IRequestFormMethods;

const FlightRequestRequestorForm: React.FC<IProps> = ({ control, errors }) => {
  const { state } = useEmployeeContext();
  const [employee, setEmployee] = useState<IEmployee>();
  const [fieldEmployee, setFieldEmployee] = useState<IEmployee>();
  const [isFieldContact, setIsFieldContact] = useState<boolean | undefined>(
    control.defaultValuesRef?.current?.request?.fieldContactFlag
  );
  const [isEmployee, setIsEmployee] = useState<boolean | undefined>(() => {
    const fieldNo = control.defaultValuesRef?.current?.request?.fieldNo;

    const value =
      fieldNo !== undefined ? fieldNo !== DEFAULT_EMPLOYEE_NUMBER : undefined;

    return value;
  });

  useEffect(() => {
    control.setValue('request.isEmployee', isEmployee, {
      shouldValidate: true,
    });
  }, [isEmployee, control]);

  const handleSearch = useCallback(
    (user, override = true) => {
      if (user) {
        control.setValue(
          'request.requestorName',
          `${user.first_name} ${user.last_name}`
        );
        control.setValue('request.requestorDept', user.dept_org_desc);
        control.setValue('request.requestorEmail', user.e_mail_address);
        control.setValue('request.requestorJobTitle', user.job_title);
        control.setValue('request.managerEmail', user.mgr_e_mail_address);
        control.setValue(
          'request.managerName',
          `${user.mgr_first_name} ${user.mgr_last_name}`
        );

        if (
          (!override &&
            !control.defaultValuesRef?.current?.request?.requestorPhone) ||
          override
        ) {
          control.setValue(
            'request.requestorPhone',
            formatPhoneNumber(user.work_phone_1)
          );
        }
      }

      setEmployee(user);
    },
    [state, control, setEmployee]
  );

  const handleField = useCallback(
    (user, override = true) => {
      if (user) {
        control.setValue(
          'request.fieldName',
          `${user.first_name} ${user.last_name}`
        );

        control.setValue('request.fieldDept', user.dept_org_desc);
        control.setValue('request.fieldCompany', user.company_name);

        if (
          (!override &&
            !control.defaultValuesRef?.current?.request?.fieldPhone) ||
          override
        ) {
          control.setValue(
            'request.fieldPhone',
            formatPhoneNumber(user.work_phone_1)
          );
        }
      }

      setFieldEmployee(user);
    },
    [state, control, setFieldEmployee]
  );

  const handleIsFieldContact = useCallback(
    (_, { value }) => {
      const isChecked = value === 'yes';
      control.setValue('request.fieldContactFlag', isChecked, {
        shouldValidate: true,
      });
      setIsFieldContact(isChecked);
    },
    [control, setIsFieldContact]
  );

  const handleIsEmployee = useCallback(
    (_, { value }) => {
      const isChecked = value === 'yes';

      setIsEmployee(isChecked);

      if (!isChecked) {
        setFieldEmployee(undefined);
      }
    },
    [setIsEmployee, setFieldEmployee]
  );

  return (
    <ATMSegment>
      <ATMHeader>{Lang.TTL_REQUEST_REQUESTOR_INFORMATION}</ATMHeader>

      <ATMGrid columns={3}>
        <ATMGrid.Column>
          <ATMField
            name="request.requestorNo"
            label={
              <LabelRequired>
                {Lang.LBL_REQUEST_REQUESTOR_EMPLOYEE}
              </LabelRequired>
            }
            control={control}
            as={DropdownEmployee}
            onChange={([_, { value }, user]: any) => {
              handleSearch(user, true);

              return value;
            }}
            onLoad={(user) => {
              handleSearch(user, false);
            }}
            icon="search"
            clearable
            error={errors.request?.requestorNo}
          />
          <ATMField
            name="request.requestorName"
            control={control}
            as={ATMInput}
            type="hidden"
            className="hidden"
          />
          <ATMField
            name="request.requestorDept"
            control={control}
            as={ATMInput}
            type="hidden"
            className="hidden"
          />
          <ATMField
            name="request.requestorJobTitle"
            control={control}
            as={ATMInput}
            type="hidden"
            className="hidden"
          />
          <ATMField
            name="request.requestorEmail"
            control={control}
            as={ATMInput}
            type="hidden"
            className="hidden"
          />
          <ATMField
            name="request.managerName"
            control={control}
            as={ATMInput}
            type="hidden"
            className="hidden"
          />
          <ATMField
            name="request.managerEmail"
            control={control}
            as={ATMInput}
            type="hidden"
            className="hidden"
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <ATMField
            name="request.requestorPhone"
            label={
              <LabelRequired>
                {Lang.LBL_REQUEST_REQUESTOR_CONTACT}
              </LabelRequired>
            }
            as={ATMInput}
            control={control}
            error={errors.request?.requestorPhone}
            isPhone
            onChange={([_, { value }]) => value}
          />
        </ATMGrid.Column>
      </ATMGrid>

      {employee && (
        <BlockEmployee className={styles.employee} data={employee} />
      )}

      <ATMGrid>
        <ATMGrid.Column>
          <ATMForm.Field error={!!errors.request?.fieldContactFlag}>
            <LabelRequired>{Lang.LBL_REQUEST_FIELD_CONTACT}</LabelRequired>
            <ATMField
              className="hidden"
              as={ATMInput}
              control={control}
              type="hidden"
              name="request.fieldContactFlag"
            />
            <ATMForm.Group>
              <ATMForm.Field>
                <ATMRadio
                  label={Lang.LBL_YES}
                  value="yes"
                  checked={isFieldContact === true}
                  onChange={handleIsFieldContact}
                />
              </ATMForm.Field>
              <ATMForm.Field>
                <ATMRadio
                  label={Lang.LBL_NO}
                  value="no"
                  checked={isFieldContact === false}
                  onChange={handleIsFieldContact}
                />
              </ATMForm.Field>
            </ATMForm.Group>
          </ATMForm.Field>
        </ATMGrid.Column>
      </ATMGrid>

      {isFieldContact === false && (
        <>
          <ATMHeader>{Lang.TTL_REQUEST_REQUESTOR_FEILD_CONTACT}</ATMHeader>

          <ATMGrid>
            <ATMGrid.Column>
              <ATMForm.Field error={!!errors.request?.isEmployee}>
                <LabelRequired>
                  {Lang.LBL_REQUEST_FIELD_CONTACT_EMPLOYEE}
                </LabelRequired>
                <ATMField
                  className="hidden"
                  as={ATMInput}
                  control={control}
                  defaultValue={false}
                  type="hidden"
                  name="request.isEmployee"
                />
                <ATMForm.Group>
                  <ATMForm.Field>
                    <ATMRadio
                      label={Lang.LBL_YES}
                      value="yes"
                      checked={isEmployee === true}
                      onChange={handleIsEmployee}
                    />
                  </ATMForm.Field>
                  <ATMForm.Field>
                    <ATMRadio
                      label={Lang.LBL_NO}
                      value="no"
                      checked={isEmployee === false}
                      onChange={handleIsEmployee}
                    />
                  </ATMForm.Field>
                </ATMForm.Group>
              </ATMForm.Field>
            </ATMGrid.Column>
          </ATMGrid>
        </>
      )}

      {!isFieldContact &&
        isEmployee !== undefined &&
        (isEmployee ? (
          <>
            <ATMGrid columns={3}>
              <ATMGrid.Column>
                <ATMField
                  name="request.fieldNo"
                  control={control}
                  label={
                    <LabelRequired>
                      {Lang.LBL_REQUEST_REQUESTOR_EMPLOYEE}
                    </LabelRequired>
                  }
                  as={DropdownEmployee}
                  onChange={([_, { value }, user]: any) => {
                    handleField(user);

                    return value;
                  }}
                  onLoad={(user) => {
                    handleField(user, false);
                  }}
                  icon="search"
                  clearable
                  error={errors.request?.fieldNo}
                />

                <ATMField
                  name="request.fieldName"
                  control={control}
                  as={ATMInput}
                  type="hidden"
                  className="hidden"
                />
                <ATMField
                  name="request.fieldDept"
                  control={control}
                  as={ATMInput}
                  type="hidden"
                  className="hidden"
                />
                <ATMField
                  name="request.fieldCompany"
                  control={control}
                  as={ATMInput}
                  type="hidden"
                  className="hidden"
                />
              </ATMGrid.Column>
              <ATMGrid.Column>
                <ATMField
                  name="request.fieldPhone"
                  label={
                    <LabelRequired>{Lang.LBL_REQUEST_CONTACT}</LabelRequired>
                  }
                  as={ATMInput}
                  control={control}
                  error={errors.request?.fieldPhone}
                  isPhone
                  onChange={([_, { value }]) => value}
                />
              </ATMGrid.Column>
            </ATMGrid>

            {fieldEmployee && (
              <BlockEmployee className={styles.employee} data={fieldEmployee} />
            )}
          </>
        ) : (
          <ATMGrid columns={4}>
            <ATMGrid.Column>
              <ATMField
                name="request.fieldNo"
                control={control}
                as={ATMInput}
                defaultValue="0"
                type="hidden"
                className="hidden"
              />
              <ATMField
                name="request.fieldName"
                label={
                  <LabelRequired>
                    {Lang.LBL_REQUEST_FIELD_CONTACT_NAME}
                  </LabelRequired>
                }
                as={ATMInput}
                control={control}
                error={errors.request?.fieldName}
              />
            </ATMGrid.Column>

            <ATMGrid.Column>
              <ATMField
                name="request.fieldCompany"
                label={Lang.LBL_REQUEST_COMPANY}
                as={ATMInput}
                control={control}
                error={errors.request?.fieldCompany}
              />
            </ATMGrid.Column>

            <ATMGrid.Column>
              <ATMField
                name="request.fieldPhone"
                label={
                  <LabelRequired>{Lang.LBL_REQUEST_CONTACT}</LabelRequired>
                }
                as={ATMInput}
                control={control}
                error={errors.request?.fieldPhone}
                isPhone
                onChange={([_, { value }]) => value}
              />
            </ATMGrid.Column>
          </ATMGrid>
        ))}
    </ATMSegment>
  );
};

export default FlightRequestRequestorForm;
